import { deleteDB } from 'idb'

/**
 * One-shot cache reset for devices that already synced.
 *
 * The app mirrors Mechanic/Electric/Relay/RelayType into IndexedDB and only
 * pulls rows newer than `lastUpdateId`. A device that synced before a data
 * migration therefore keeps serving its stale copy forever — it never asks the
 * server for rows it thinks it already has.
 *
 * Bumping CACHE_VERSION makes each browser drop its cache exactly once, on its
 * next load, and then re-sync from scratch. Browsers already carrying the
 * current stamp — and brand new ones after their first load — skip it, so this
 * is not a repeating "always refresh" behaviour.
 *
 * Bump this ONLY when cached rows are known to be wrong. Every bump costs each
 * client a full re-sync.
 */
const CACHE_VERSION = '2026-07-25-header-hierarchy-rebuild'

const VERSION_KEY = 'cacheVersion'
const DB_NAME = 'relayDB'
const LAST_UPDATE_KEY = 'lastUpdateId'

export async function resetStaleCache(): Promise<boolean> {
	let stored: string | null = null
	try {
		stored = localStorage.getItem(VERSION_KEY)
	} catch {
		// private mode / storage disabled — nothing is cached, nothing to reset
		return false
	}

	if (stored === CACHE_VERSION) return false

	try {
		await deleteDB(DB_NAME, {
			// Another open tab holds a connection. Reloading it is the user's
			// call; the stamp stays unwritten so the reset retries next load.
			blocked() {
				console.warn(
					'[cache] relayDB delete is blocked by another open tab — close it and reload'
				)
			}
		})
		localStorage.removeItem(LAST_UPDATE_KEY)
		localStorage.setItem(VERSION_KEY, CACHE_VERSION)
		return stored !== null
	} catch (error) {
		// Leave the stamp unwritten so the next load tries again rather than
		// running on a half-cleared cache.
		console.error('[cache] failed to reset local cache', error)
		return false
	}
}
